import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { Icon } from '@iconify/react';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';
import checkboxBlankOutline from '@iconify/icons-mdi/checkbox-blank-outline';
import checkboxMarked from '@iconify/icons-mdi/checkbox-marked';
import checkboxMultipleMarked from '@iconify/icons-mdi/checkbox-multiple-marked';

import Theme from '../common/NekoTheme';
import NekoBusyOverlay from '../common/NekoBusyOverlay';

const Table = Styled.table`
  background-color: ${Theme.white};
  font-family: ${Theme.fontFamily};
  border-spacing: 0;
  width: 100%;

  th, td {
    margin: 0;
    padding: 5px;
    border-bottom: 1px solid rgba(${Theme.blackRGB}, 0.1);
    border-right: 1px solid rgba(${Theme.blackRGB}, 0.1);

    a {
      text-decoration: none;
    }

    :last-child {
      border-right: 0;
    }
  }

  th, tfoot td {
    height: 30px;
    background-color: ${Theme.blue};
    color: ${Theme.white};
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 16px;
    text-align: left;

    div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &.sortable {
        cursor: pointer;
      }
    }
  }

  tbody {
    tr {
      :nth-child(even) {
        background-color: ${Theme.tableGray};
      }
      &.selected {
        background-color: rgba(${Theme.blueRGB}, 0.8);
        color: ${Theme.white};

        a {
          color: #81e8ff;
        }
      }
    }
    img {
      vertical-align: bottom;
    }
  }

  tfoot tr:last-child {
    td {
      border-bottom: 0;
    }
  }

  .table-checkbox-cell {
    width: 35px;
    text-align: center;

    svg {
      padding: 5px;
      cursor: pointer;
    }
  }
`;

const TableCheckBox = (props) => {
  const { checked, intermediate = false, onSelect = () => {}, onUnselect = () => {}, ...rest } = props;

  const onClick = (e) => { 
    checked ? onUnselect(e) : onSelect(e);
  };

  return (
    <Icon icon={intermediate ? checkboxMultipleMarked : (checked ? checkboxMarked : checkboxBlankOutline)}
      width="24px" height="24px" onClick={onClick} />
  );
};

/**
 * @param {string} className
 * @param {string|object} icon
 * @param {bool} disabled
 * @param {function} onClick
 */
const StyledNekoTable = (props) => {
  const { columns, data, busy = false, onSelect, onUnselect, selectedItems, sort, onSortChange } = props;
  const columnsCount = columns.length + (onSelect ? 1 : 0);

  const rows = data.map(v => {
    const cells = columns.map(c => { return { value: v[c.accessor], style : c.style || {} }});
    return { id: v.id, cells };
  });

  const currentRowsIds = rows.map(x => x.id);
  const currentSelectedRows = currentRowsIds.filter(x => selectedItems.includes(x));
  const areAllRowsSelected = currentSelectedRows.length === currentRowsIds.length;
  const isIntermediate = !areAllRowsSelected && selectedItems.length > 0;

  const hiddenColumnIndexes = columns.reduce(function(a, e, i) {
    if (e.visible === false)
      a.push(i);
    return a;
  }, []);

  const headersFooters = <tr>
    {onSelect && 
      <th className='table-checkbox-cell'>
        <TableCheckBox checked={areAllRowsSelected} intermediate={isIntermediate}
          onSelect={(e) => onSelect(currentRowsIds, e) } 
          onUnselect={(e) => { 
            // The unselect on selectedItems never happens, but I keep it here in case
            // we find an elegant UI solution to unselect all the invisible selected rows as well.
            isIntermediate ? onUnselect(selectedItems, e) : onUnselect(currentRowsIds, e);
          }}
        />
      </th>
    }
    {columns.filter((x, i) => !hiddenColumnIndexes.includes(i)).map(column => {
      let beingSorted = sort && sort.accessor === column.accessor;
      let beingSortedAsc = sort && sort.by === 'asc';
      const thStyle = column.style || {};
      return (<th style={thStyle} key={column.accessor}>
        <div className={column.sortable ? 'sortable' : ''} 
          onClick={column.sortable ? (e) => { 
            onSortChange(column.accessor, beingSorted && beingSortedAsc ? 'desc' : 'asc', e)
          } : undefined}>
          <div>{column.title}</div>
          <div>{column.sortable && 
            <Icon icon={!beingSorted ? chevronDown : (beingSortedAsc ? chevronDown : chevronUp)} 
              color={beingSorted ? Theme.white : `rgba(${Theme.blackRGB}, 0.1)`} 
              width="26px" height="26px" />}
          </div>
        </div>
      </th>)
    })}
  </tr>;

  return (
    <NekoBusyOverlay busy={busy} overlayStyle={{ top: '36px', height: 'calc(100% - 76px)' }}>
      <Table>
        <thead>{headersFooters}</thead>
        <tbody>
          {!rows.length && 
            <tr><td colspan={columnsCount} style={{ textAlign: 'center', height: 40, color: 'gray' }}>
              Empty.
            </td></tr>
          }
          {rows.map(row => {
            return (
              <tr className={selectedItems.includes(row.id) ? 'selected' : ''}>
                {onSelect && 
                  <td className='table-checkbox-cell'>
                    <TableCheckBox 
                      checked={selectedItems.includes(row.id)}
                      onSelect={(e) => onSelect([ row.id ], e)}
                      onUnselect={(e) => onUnselect([ row.id ], e)} 
                    />
                  </td>
                }
                {row.cells.filter((x, i) => !hiddenColumnIndexes.includes(i)).map(cell => {
                  return <td style={cell.style}>{cell.value}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>{headersFooters}</tfoot>
      </Table>
    </NekoBusyOverlay>
  );
};

const NekoTable = (props) => {
  return (<StyledNekoTable {...props} />);
};

NekoTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.any),
  data: PropTypes.arrayOf(PropTypes.any),
  busy: PropTypes.arrayOf(PropTypes.bool),
  onSelect: PropTypes.func,
  onUnselect: PropTypes.func,
  selectedItems: PropTypes.arrayOf(PropTypes.object),
  onSortChange: PropTypes.func
};

NekoTable.defaultProps = {
  columns: undefined,
  data: undefined,
  busy: false,
  onSelect: undefined,
  onUnselect: undefined,
  selectedItems: undefined,
  onSortChange: undefined
};

export { NekoTable };
